import clsx, { type ClassValue } from "clsx";
import { marked } from "marked";
import { Fragment, type RefObject } from "react";
import { redirect, type LoaderFunction, type LoaderFunctionArgs } from "react-router";
import { twMerge } from "tailwind-merge";

import { AUTH_COOKIE_NAME, taco_cookie_ctx } from "@/auth-cookie";
import { showSnackbar } from "@/hooks/snackbar";
import type { User } from "@/types/user";

// An abort is expected on unmount, so it is not a failure anyone needs to see.
const isAbortError = (err: unknown) => err instanceof DOMException && err.name === "AbortError";

// The api modules resolve { data, error } only for responses that actually
// arrived. fetch still rejects on a network failure or on an abort, and those
// rejections have nowhere to go in a fire-and-forget call, so they surface as
// unhandled promise rejections. An abort is expected on unmount, the rest is
// worth seeing in the console.
export const logRejection = (err: unknown) => {
  if (isAbortError(err)) {
    return;
  }
  console.error(err);
};

// React event props want a void return, so an async handler passed straight to
// one leaves its rejection unhandled. Wrap it here instead: the handler stays
// async, the prop gets a sync function, and the rejection reaches logRejection.
//
// A rejection that gets this far is a network failure or a bug, never an API
// error the handler chose to report, so the message stays generic. Call sites
// know what was being attempted and say so themselves.
export const handled =
  <A extends unknown[]>(fn: (...args: A) => Promise<unknown>) =>
  (...args: A): void => {
    fn(...args).catch((err: unknown) => {
      logRejection(err);
      if (!isAbortError(err)) {
        showSnackbar("Something went wrong. Please try again.", "warning");
      }
    });
  };

export const hasRole = (user: User | null | undefined, role: string) => {
  return (user?.roles && user.roles.includes(role)) || false;
};

export const prettyRole = (role: string) => {
  switch (role) {
    case "assessor":
      return "Assessor";
    case "full_access":
      return "Full access";
    case "admin":
      return "Admin";
    default:
      return "";
  }
};

// Deep-merges `partial` into a copy of `original`: a nested object recurses, a
// primitive overwrites. The indexed writes need casts because a key that only
// exists as a runtime string cannot be tied back to T's property types.
export const partialObjectUpdater = <T extends object>(original: T, partial: Partial<T>): T => {
  const copy = { ...original };
  for (const key of Object.keys(partial) as (keyof T)[]) {
    const value = partial[key];
    if (value && typeof value === "object") {
      copy[key] = partialObjectUpdater(copy[key] as object, value) as T[keyof T];
    } else {
      copy[key] = value as T[keyof T];
    }
  }
  return copy;
};

export const copyTextToClipboard = (text: string) => {
  return navigator.clipboard.writeText(text).then(
    () => true,
    (err) => {
      console.error("Async: Could not copy text: ", err);
      return false;
    },
  );
};

export const scrollToBottom = (ref: RefObject<HTMLElement | null>) => {
  ref.current?.scrollIntoView({
    block: "end",
    inline: "nearest",
    behavior: "smooth",
  });
};

export const prettyDateString = (datestring: string) => {
  // by adding UTC, we can parse the datestring as UTC time
  const date = new Date(Date.parse(`${datestring} UTC`));
  return date.toLocaleString().replace("T", " ").split(".")[0];
};

function getExtension(file_type: string) {
  const parts = file_type.split("/");
  return parts[parts.length - 1];
}

export const isImage = (file_type: string) => {
  const ext = getExtension(file_type);
  switch (ext?.toLowerCase()) {
    case "jpg":
    case "jpeg":
    case "png":
    case "webp":
      return true;
  }
  return false;
};

export const nl2br = (content: string) => {
  return content.split("\n").map((item, key) => {
    return (
      <Fragment key={key}>
        {item}
        <br />
      </Fragment>
    );
  });
};

export const renderMarkdown = (content: string) => {
  return { __html: marked.parse(content) };
};

export const boxclass = clsx("rounded-md border border-slate-800 bg-slate-900 p-4 shadow-md");

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function authWrapper(loader: LoaderFunction) {
  return (args: LoaderFunctionArgs) => {
    const { request } = args;
    const jwt = taco_cookie_ctx.get(AUTH_COOKIE_NAME);
    if (!jwt) {
      const url = new URL(request.url);
      const params = new URLSearchParams();
      params.set("to", url.pathname + url.search);
      throw redirect(`/login?${params.toString()}`);
    }
    return loader(args);
  };
}
